import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { useSelector, useDispatch } from 'react-redux'

// Actions
import { sendRequestForDirectMessage } from '../../../../actions/homeowner'

// Constants
import { CUSTOMER_MESSAGES } from '../../../../constants/customerMessages'

// Components
import { Page, If, List, Text, Unless, OutreachButton } from '../../..'
import { OverlayQuestionsFooter } from '../../../shared/Routing'
import { Player } from '../../../shared/Player'

// Selectors
import { selectDmFailure, selectDmRequest, selectDmSuccess } from '../../../../store/selectors/homeowner'
import {
  selectReverseMortgageVideoEnabled,
  selectReverseMortgageVideoExternalId,
  selectReverseMortgageVideoType
} from '../../../../store/selectors/customerProfile'

// Assets
import reverseMortgageGif from './gifs/reverse-mortgage.gif'
import LoanSvg from './svgs/LoanSvg'
import HelocSvg from './svgs/HelocSvg'
import DownsizingSvg from './svgs/DownsizingSvg'

// Styles
import styles from './ReverseMortgagePanel.module.scss'
import { useTrackingContext } from '../../../../providers/tracking'

const buttonMessages = defineMessages({
  buttonText: {
    id: 'ReverseMortgage.requestMoreButton.initial',
    defaultMessage: 'Request more details'
  },
  buttonSuccess: {
    id: 'ReverseMortgage.requestMoreButton.success',
    defaultMessage: 'Message sent!'
  },
  buttonFailure: {
    id: 'ReverseMortgage.requestMoreButton.failure',
    defaultMessage: 'Please try again later!'
  }
})

const ReverseMortgagePanel = () => {
  const videoEnabled = useSelector(selectReverseMortgageVideoEnabled)
  const videoId = useSelector(selectReverseMortgageVideoExternalId)
  const videoType = useSelector(selectReverseMortgageVideoType)
  const dispatch = useDispatch()
  const dmRequest = useSelector(selectDmRequest)
  const dmSuccess = useSelector(selectDmSuccess)
  const dmFailure = useSelector(selectDmFailure)
  const intl = useIntl()
  const { trackingClient } = useTrackingContext()

  const handleClick = () => {
    dispatch(
      sendRequestForDirectMessage(
        intl.formatMessage(CUSTOMER_MESSAGES.REVERSE_MORTGAGE.message),
        CUSTOMER_MESSAGES.REVERSE_MORTGAGE.topic,
        CUSTOMER_MESSAGES.REVERSE_MORTGAGE.topicKey,
        trackingClient
      )
    )
  }

  return (
    <Page>
      <Page.Body data-qa='reverseMortgagePanel'>
        <Text className={styles.panelTitle} size='xl' variant='neutralCool' variantCount='900'>
          <FormattedMessage
            id='reverseMortgage.panelTitle'
            defaultMessage="A reverse mortgage may allow you to convert your home's equity into cash"
          />
        </Text>
        <div>
          <If condition={videoEnabled}>
            <Player videoType={videoType} videoId={videoId} className={styles.videoPlayer} />
          </If>
          <Unless condition={videoEnabled}>
            <img src={reverseMortgageGif} alt='piggy bank!' className={styles.reverseMortgageImage} />
          </Unless>
        </div>

        <Text className={styles.listTitle} size='l' weight='semibold' variant='neutralCool' variantCount='900'>
          <FormattedMessage id='ReverseMortgage.listTitle' defaultMessage='A few things to know:' />
        </Text>
        <List.Bulleted className={styles.bulletList}>
          <FormattedMessage
            id='ReverseMortgage.bullet1'
            defaultMessage='These funds can be used however you choose. Common uses include supplementing income, saving for retirement, vacationing, repairing or improving your property, or paying for medical expenses'
          />

          <FormattedMessage
            id='ReverseMortgage.bullet2'
            defaultMessage='You have options to use your equity, however keep in mind the mortgage must be paid off when your home is sold, you move out, or pass away'
          />

          <FormattedMessage
            id='ReverseMortgage.bullet3'
            defaultMessage='Keep in mind that you must continue paying ongoing property expenses such as taxes, homeowner’s insurance, homeowner association (HOA) dues, and costs associated with maintaining your property'
          />
        </List.Bulleted>

        <div className={styles.otherOptions}>
          <Text className={styles.otherOptionsTitle} size='l' variant='neutralCool' variantCount='900'>
            <FormattedMessage
              id='ReverseMortgage.otherOptionsTitle'
              defaultMessage='Not sure about a reverse mortgage? You could consider:'
            />
          </Text>
          <div className={styles.row}>
            <DownsizingSvg />

            <div className={styles.rowContent}>
              <Text variant='neutralCool' variantCount='900'>
                <FormattedMessage id='ReverseMortgage.downsizing.title' defaultMessage='Downsizing' />
              </Text>

              <Text variant='neutralCool' variantCount='700'>
                <FormattedMessage
                  id='ReverseMortgage.downsizing.body'
                  defaultMessage='Buy a different home with a lower mortgage payment'
                />
              </Text>
            </div>
          </div>

          <div className={styles.row}>
            <LoanSvg />

            <div className={styles.rowContent}>
              <Text variant='neutralCool' variantCount='900'>
                <FormattedMessage id='ReverseMortgage.cashout.title' defaultMessage='A Cashout refinance' />
              </Text>

              <Text variant='neutralCool' variantCount='700'>
                <FormattedMessage
                  id='ReverseMortgage.cashout.body'
                  defaultMessage='Borrow against your current equity amount'
                />
              </Text>
            </div>
          </div>

          <div className={styles.row}>
            <HelocSvg />

            <div className={styles.rowContent}>
              <Text variant='neutralCool' variantCount='900'>
                <FormattedMessage id='ReverseMortgage.heloc.title' defaultMessage='A HELOC' />
              </Text>

              <Text variant='neutralCool' variantCount='700'>
                <FormattedMessage
                  id='ReverseMortgage.heloc.body'
                  defaultMessage='Get a line of credit using your current equity'
                />
              </Text>
            </div>
          </div>
          <OutreachButton
            handleClick={handleClick}
            success={dmSuccess}
            failure={dmFailure}
            requested={dmRequest}
            buttonMessage={buttonMessages.buttonText}
            resultMessage={
              dmSuccess
                ? intl.formatMessage(buttonMessages.buttonSuccess)
                : intl.formatMessage(buttonMessages.buttonFailure)
            }
          />
        </div>
      </Page.Body>
      <Text variant='neutralCool' variantCount='700' size='xs' element='i'>
        <FormattedMessage
          id='reverseMortgage.disclaimer'
          defaultMessage='These materials are not from HUD or FHA and were not approved by HUD or a government agency.'
        />
      </Text>
      <Page.Footer showDisclaimer>
        <OverlayQuestionsFooter
          topic={CUSTOMER_MESSAGES.REVERSE_MORTGAGE.topic}
          topicKey={CUSTOMER_MESSAGES.REVERSE_MORTGAGE.topicKey}
          questions={[CUSTOMER_MESSAGES.REVERSE_MORTGAGE.secondaryMessage]}
          placeholder
        />
      </Page.Footer>
    </Page>
  )
}

export default ReverseMortgagePanel
